import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X } from "lucide-react";
import { adminApi } from "@/lib/api";
import { formatApiError } from "@/admin/AdminAuthContext";

const EMPTY = {
  slug: "", name: "", tagline: "", description: "", icon: "Briefcase",
  logo_path: "", overview: "",
  services: [], sectors: [], strengths: [],
  display_order: 1, active: true,
};

export default function AdminSubsidiaries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // record or null; new = {...EMPTY}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/subsidiaries");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const isNew = !editing.id;
    try {
      if (isNew) {
        await adminApi.post("/admin/subsidiaries", editing);
        toast.success("Subsidiary added.");
      } else {
        const { id, ...body } = editing;
        await adminApi.patch(`/admin/subsidiaries/${id}`, body);
        toast.success("Subsidiary updated.");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this subsidiary?")) return;
    try {
      await adminApi.delete(`/admin/subsidiaries/${id}`);
      toast.success("Deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-subsidiaries-page">
      <PageHeader title="Subsidiaries" onAdd={() => setEditing({ ...EMPTY })} testId="add-subsidiary" />

      {loading ? <LoadingRow /> : (
        <div className="bg-white border border-softgray divide-y divide-softgray">
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-4 p-4 hover:bg-offwhite/70" data-testid={`subsidiary-row-${s.slug}`}>
              <div className="w-14 h-14 flex items-center justify-center border border-softgray bg-white shrink-0">
                {s.logo_path
                  ? <img src={`${process.env.REACT_APP_BACKEND_URL}${s.logo_path}`} alt="" className="max-h-10 max-w-10 object-contain" />
                  : <span className="text-mutedink text-[10px]">—</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-navy leading-tight truncate">{s.name}</div>
                <div className="text-mutedink text-xs mt-0.5">{s.tagline} · order {s.display_order} · {s.active ? "active" : "hidden"}</div>
              </div>
              <button className="btn-outline-gold !py-1.5 !text-[10px]" onClick={() => setEditing(s)} data-testid={`edit-subsidiary-${s.slug}`}>Edit</button>
              <button className="btn-outline-gold !py-1.5 !text-[10px] !border-red-300 !text-red-700" onClick={() => remove(s.id)}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="p-6 text-mutedink text-sm text-center">No subsidiaries yet.</div>}
        </div>
      )}

      {editing && (
        <Drawer title={editing.id ? "Edit subsidiary" : "New subsidiary"}
                onClose={() => setEditing(null)} onSave={save} testId="subsidiary-drawer">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Slug" required value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
            <Field label="Name" required value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            <Field label="Tagline" value={editing.tagline} onChange={(v) => setEditing({ ...editing, tagline: v })} />
            <Field label="Icon (lucide name)" value={editing.icon} onChange={(v) => setEditing({ ...editing, icon: v })} />
            <Field label="Logo path (URL under /api/media/…)" value={editing.logo_path} onChange={(v) => setEditing({ ...editing, logo_path: v })}
                   className="sm:col-span-2" />
            <Field label="Short description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })}
                   className="sm:col-span-2" />
            <FieldTextarea label="Overview (long paragraph)" rows={4} value={editing.overview} onChange={(v) => setEditing({ ...editing, overview: v })}
                           className="sm:col-span-2" />
            <FieldTextarea label="Services — one per line" rows={5}
                           value={(editing.services || []).join("\n")}
                           onChange={(v) => setEditing({ ...editing, services: v.split(/\n+/).map(x => x.trim()).filter(Boolean) })}
                           className="sm:col-span-2" />
            <FieldTextarea label="Sectors — one per line" rows={3}
                           value={(editing.sectors || []).join("\n")}
                           onChange={(v) => setEditing({ ...editing, sectors: v.split(/\n+/).map(x => x.trim()).filter(Boolean) })} />
            <FieldTextarea label="Strengths — one per line" rows={3}
                           value={(editing.strengths || []).join("\n")}
                           onChange={(v) => setEditing({ ...editing, strengths: v.split(/\n+/).map(x => x.trim()).filter(Boolean) })} />
            <Field label="Display order" type="number" value={editing.display_order}
                   onChange={(v) => setEditing({ ...editing, display_order: parseInt(v || "0", 10) })} />
            <Checkbox label="Active (visible on site)" checked={editing.active}
                      onChange={(v) => setEditing({ ...editing, active: v })} />
          </div>
        </Drawer>
      )}
    </div>
  );
}

// ============= shared shells ============ //
export function PageHeader({ title, onAdd, testId }) {
  return (
    <div className="mb-8 flex items-end justify-between">
      <div>
        <div className="text-gold text-[11px] uppercase tracking-[0.28em] font-medium mb-2">Manage</div>
        <h1 className="font-serif text-navy text-3xl md:text-4xl">{title}</h1>
      </div>
      {onAdd && (
        <button className="btn-gold" onClick={onAdd} data-testid={testId}>
          <Plus size={14} /> Add new
        </button>
      )}
    </div>
  );
}

export function LoadingRow() {
  return <div className="bg-white border border-softgray p-8 text-center text-mutedink text-sm">Loading…</div>;
}

export function Drawer({ title, testId, onClose, onSave, children }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid={testId}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative ml-auto w-full max-w-2xl bg-white h-full flex flex-col shadow-2xl">
        <header className="flex items-center justify-between px-6 py-4 border-b border-softgray">
          <h2 className="font-serif text-navy text-xl">{title}</h2>
          <button onClick={onClose} className="text-mutedink hover:text-navy p-2" data-testid="drawer-close"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
        <footer className="px-6 py-4 border-t border-softgray flex gap-3 justify-end">
          <button className="btn-outline-gold" onClick={onClose}>Cancel</button>
          <button className="btn-gold" onClick={onSave} data-testid="drawer-save"><Save size={14} /> Save</button>
        </footer>
      </aside>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", required, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">
        {label} {required && <span className="text-gold">*</span>}
      </label>
      <input type={type} required={required} value={value ?? ""}
             onChange={(e) => onChange(e.target.value)}
             className="w-full border border-softgray px-3 py-2 text-navy text-sm outline-none focus:border-gold" />
    </div>
  );
}

export function FieldTextarea({ label, value, onChange, rows = 3, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-navy text-xs uppercase tracking-[0.18em] font-semibold mb-2">{label}</label>
      <textarea rows={rows} value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-softgray px-3 py-2 text-navy text-sm outline-none focus:border-gold resize-y" />
    </div>
  );
}

export function Checkbox({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 text-sm text-navy self-end pb-2">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
             className="w-4 h-4 accent-gold" />
      {label}
    </label>
  );
}
